import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Search, Wrench, PanelLeftClose, PanelLeftOpen, RefreshCw,
    AlertTriangle, AlertOctagon, Inbox, ChevronRight, Loader2, Factory, X,
    Users // ✅ Добавляем иконку для групп
} from 'lucide-react';
import DeviceTree from '../../../components/DeviceTree';
import Aksioma01Page from '../devices/aksioma01/Aksioma01Page';
import Aksioma03Page from '../devices/aksioma03/Aksioma03Page';
import Karat307Page from '../devices/karat307/Karat307Page';
import StreetPage from '../devices/StreetPage';
import HousePage from '../devices/HousePage';

const DevicesPage = () => {
    const [selectedNode, setSelectedNode] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [searchError, setSearchError] = useState(null);
    const [expandedPath, setExpandedPath] = useState(null);
    const [devices, setDevices] = useState({
        all: {},
        onService: {},
        connected: {},
        counts: { all: 0, onService: 0, connected: 0 }
    });
    const [groups, setGroups] = useState([]); // ✅ Новое состояние для групп
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
    const [isTreeVisible, setIsTreeVisible] = useState(true);

    useEffect(() => {
        const storedDeviceStr = localStorage.getItem('selectedDeviceFromMap');
        if (storedDeviceStr) {
            try {
                const device = JSON.parse(storedDeviceStr);
                setSelectedNode({ type: 'device', data: device });
                setExpandedPath({
                    city: device.city,
                    street: device.street,
                    house: `Дом ${device.houseNumber || '?'}`,
                });
                localStorage.removeItem('selectedDeviceFromMap');
            } catch (e) {
                console.error('Ошибка парсинга устройства из localStorage:', e);
            }
        }

        const storedHouseStr = localStorage.getItem('selectedHouseFromStats');
        if (storedHouseStr) {
            try {
                const house = JSON.parse(storedHouseStr);
                const houseLabel = `Дом ${house.houseNumber || '?'}`;
                setSelectedNode({
                    type: 'house',
                    data: { city: house.city, street: house.street, house: houseLabel },
                });
                setExpandedPath({
                    city: house.city,
                    street: house.street,
                    house: houseLabel,
                });
                localStorage.removeItem('selectedHouseFromStats');
            } catch (e) {
                console.error('Ошибка парсинга дома из localStorage:', e);
            }
        }

        const handler = (e) => {
            const device = e.detail?.device;
            if (device) {
                setSelectedNode({ type: 'device', data: device });
                setExpandedPath({
                    city: device.city,
                    street: device.street,
                    house: `Дом ${device.houseNumber || '?'}`,
                });
            }
        };
        window.addEventListener('selectDeviceFromMap', handler);
        return () => {
            window.removeEventListener('selectDeviceFromMap', handler);
            localStorage.removeItem('selectedDeviceFromMap');
            localStorage.removeItem('selectedHouseFromStats');
        };
    }, []);

    useEffect(() => {
        loadDevices();
        const handleResize = () => {
            const newIsDesktop = window.innerWidth >= 1024;
            setIsDesktop((prevIsDesktop) => {
                if (prevIsDesktop && !newIsDesktop && !isTreeVisible) {
                    setIsTreeVisible(true);
                }
                return newIsDesktop;
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isTreeVisible]);

    const loadDevices = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';

            // ✅ Параллельно загружаем устройства и группы
            const [devicesResponse, groupsResponse] = await Promise.all([
                axios.get(`${API_URL}/api/devices`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                axios.get(`${API_URL}/api/devicegroups`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }).catch(err => {
                    console.warn('Не удалось загрузить группы:', err);
                    return { data: [] }; // Если группы не загрузились - пустой массив
                })
            ]);

            const data = devicesResponse.data;
            const groupsData = groupsResponse.data || [];

            const buildTree = (deviceList) => {
                const tree = {};
                deviceList.forEach(device => {
                    const city = device.city || 'Неизвестный город';
                    let street = device.street || 'Неизвестная улица';
                    if (street.includes(city)) {
                        street = street.replace(city, '').trim();
                    }
                    const house = `Дом ${device.houseNumber || '?'}`;
                    const deviceFullName = `${device.deviceType} ${device.name}`;
                    if (!tree[city]) tree[city] = {};
                    if (!tree[city][street]) tree[city][street] = {};
                    if (!tree[city][street][house]) tree[city][street][house] = [];
                    tree[city][street][house].push({
                        ...device,
                        fullName: deviceFullName
                    });
                });
                return tree;
            };

            // ✅ Создаём карту всех устройств по имени для быстрого поиска
            const allDevicesMap = {};
            (data.allDevices || []).forEach(device => {
                allDevicesMap[device.name] = {
                    ...device,
                    fullName: `${device.deviceType} ${device.name}`
                };
            });

            // ✅ Преобразуем группы в деревья устройств
            const groupsWithTrees = groupsData.map(group => {
                const groupDevices = [];
                group.devices.forEach(deviceName => {
                    const device = allDevicesMap[deviceName];
                    if (device) {
                        groupDevices.push(device);
                    }
                });
                return {
                    ...group,
                    tree: buildTree(groupDevices),
                    deviceCount: groupDevices.length
                };
            });

            setDevices({
                all: buildTree(data.allDevices || []),
                onService: buildTree(data.onServiceDevices || []),
                connected: buildTree(data.connectedDevices || []),
                counts: {
                    all: data.countAll || 0,
                    onService: data.countOnService || 0,
                    connected: data.countConnected || 0
                }
            });

            setGroups(groupsWithTrees);
        } catch (err) {
            console.error('Ошибка загрузки устройств:', err);
            setError('Не удалось загрузить список устройств');
        } finally {
            setIsLoading(false);
        }
    };

    const filterTree = (tree, search) => {
        if (!search) return tree;
        const searchLower = search.toLowerCase();
        const filtered = {};
        Object.keys(tree).forEach(city => {
            Object.keys(tree[city]).forEach(street => {
                Object.keys(tree[city][street]).forEach(house => {
                    tree[city][street][house].forEach(device => {
                        if (
                            device.fullName.toLowerCase().includes(searchLower) ||
                            device.name.toLowerCase().includes(searchLower) ||
                            city.toLowerCase().includes(searchLower) ||
                            street.toLowerCase().includes(searchLower) ||
                            house.toLowerCase().includes(searchLower)
                        ) {
                            if (!filtered[city]) filtered[city] = {};
                            if (!filtered[city][street]) filtered[city][street] = {};
                            if (!filtered[city][street][house]) filtered[city][street][house] = [];
                            filtered[city][street][house].push(device);
                        }
                    });
                });
            });
        });
        return filtered;
    };

    const findExactDevice = (tree, targetName) => {
        const lowerTarget = targetName.toLowerCase().trim();
        for (const city of Object.keys(tree)) {
            for (const street of Object.keys(tree[city])) {
                for (const house of Object.keys(tree[city][street])) {
                    const foundDevice = tree[city][street][house].find(
                        d => d.name.toLowerCase() === lowerTarget || d.fullName.toLowerCase() === lowerTarget
                    );
                    if (foundDevice) {
                        return { device: foundDevice, path: { city, street, house } };
                    }
                }
            }
        }
        return null;
    };

    const handleNodeSelect = (node) => {
        if (!node || !node.type) return;
        setSelectedNode(node);
        setSearchError(null);
    };

    const handleBackToList = () => {
        setSelectedNode(null);
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            const query = searchText.trim();
            if (!query) return;
            const result = findExactDevice(devices.all, query);
            if (result) {
                setSelectedNode({ type: 'device', data: result.device });
                setExpandedPath(result.path);
                setSearchError(null);
            } else {
                setSelectedNode(null);
                setExpandedPath(null);
                setSearchError('Устройство не найдено');
            }
        }
    };

    const handleClearSearch = () => {
        setSearchText('');
        setSearchError(null);
    };

    const filteredDevices = {
        all: filterTree(devices.all, searchText),
        onService: filterTree(devices.onService, searchText),
        connected: filterTree(devices.connected, searchText)
    };

    // ✅ Фильтрация групп
    const filteredGroups = groups.map(group => ({
        ...group,
        tree: filterTree(group.tree, searchText)
    }));

    const renderSelectedNode = () => {
        if (!selectedNode) {
            return (
                <div className="flex flex-col items-center justify-center text-center py-4 sm:py-12">
                    <div className="text-slate-500 mb-2 sm:mb-4">
                        <Search className="w-12 h-12 sm:w-16 sm:h-16" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-sm sm:text-xl font-bold text-white mb-1 sm:mb-2">Выберите объект</h3>
                    <p className="text-slate-400 text-xs sm:text-sm px-4">
                        Выберите устройство, улицу или дом из списка для просмотра подробной информации
                    </p>
                </div>
            );
        }
        switch (selectedNode.type) {
            case 'device':
                const device = selectedNode.data;
                switch (device.deviceType) {
                    case 'Аксиома-01':
                        return <Aksioma01Page device={device} onBack={handleBackToList} isDesktop={isDesktop} onSelectDevice={handleNodeSelect} />;
                    case 'Аксиома-03':
                        return <Aksioma03Page device={device} onBack={handleBackToList} isDesktop={isDesktop} onSelectDevice={handleNodeSelect} />;
                    case 'Карат-307':
                        return <Karat307Page device={device} onBack={handleBackToList} isDesktop={isDesktop} onSelectDevice={handleNodeSelect} />;
                    default:
                        return (
                            <div className="p-6 text-white">
                                <div className="text-slate-400 mb-4"><Wrench className="w-16 h-16 mx-auto" strokeWidth={1.5} /></div>
                                <h3 className="text-xl font-bold mb-2 text-center">Неизвестный тип устройства</h3>
                                <p className="text-slate-400 text-center">Тип: {device.deviceType || 'не определён'}</p>
                            </div>
                        );
                }
            case 'street':
                return <StreetPage city={selectedNode.data.city} street={selectedNode.data.street} onBack={handleBackToList} isDesktop={isDesktop} onSelect={handleNodeSelect} />;
            case 'house':
                return <HousePage city={selectedNode.data.city} street={selectedNode.data.street} house={selectedNode.data.house} onBack={handleBackToList} isDesktop={isDesktop} onSelectDevice={handleNodeSelect} onSelect={handleNodeSelect} />;
            default:
                return null;
        }
    };

    const getTreeStyle = () => {
        if (!isTreeVisible) {
            return { width: '0px', maxWidth: '0px', opacity: 0, borderWidth: '0px', padding: '0px', overflow: 'hidden', transition: 'all 0.3s ease-in-out' };
        }
        if (isDesktop) {
            return { width: '33.333%', maxWidth: '400px', opacity: 1, borderWidth: '1px', transition: 'all 0.3s ease-in-out' };
        }
        return { width: '100%', maxWidth: '100%', opacity: 1, borderWidth: '1px', transition: 'all 0.3s ease-in-out' };
    };

    if (!isDesktop && selectedNode) {
        return (
            <div className="space-y-3">
                <div className="h-[calc(100vh-140px)] bg-slate-900 rounded-xl overflow-hidden border border-slate-700">
                    {renderSelectedNode()}
                </div>
            </div>
        );
    }

    // ✅ Определяем, нужно ли сворачивать стандартные деревья
    const hasGroups = filteredGroups.length > 0;
    const defaultStandardTreesOpen = !hasGroups; // Если есть группы - сворачиваем

    return (
        <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                    <Factory className="w-6 h-6 sm:w-7 sm:h-7 text-blue-400" strokeWidth={2} />
                    Объект учета
                </h2>
                <div className="flex items-center gap-2">
                    {isDesktop && (
                        <button onClick={() => setIsTreeVisible(!isTreeVisible)} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white rounded-lg text-sm font-medium transition flex items-center gap-2">
                            {isTreeVisible ? <><PanelLeftClose className="w-4 h-4" /><span>Скрыть список</span></> : <><PanelLeftOpen className="w-4 h-4" /><span>Показать список</span></>}
                        </button>
                    )}
                    <button onClick={loadDevices} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" /> Обновить
                    </button>
                </div>
            </div>
            {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
            )}
            <div className={`flex gap-4 sm:gap-6 ${isDesktop ? 'flex-row' : 'flex-col'}`}>
                <div className="bg-slate-800 rounded-xl border border-slate-700 flex flex-col flex-shrink-0" style={{ ...getTreeStyle(), maxHeight: isTreeVisible ? 'calc(100vh - 200px)' : '0px' }}>
                    <div className="p-3 border-b border-slate-700 flex-shrink-0">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Поиск устройства..."
                                value={searchText}
                                onChange={(e) => {
                                    setSearchText(e.target.value);
                                    setSearchError(null);
                                }}
                                onKeyDown={handleSearchKeyDown}
                                className="w-full px-3 py-2 pl-9 pr-9 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-3" />
                            {searchText && (
                                <button
                                    onClick={handleClearSearch}
                                    className="absolute right-3 top-2.5 text-slate-500 hover:text-white transition"
                                    title="Очистить поиск"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        {searchError && (
                            <div className="text-red-400 text-xs mt-2 px-1 flex items-center gap-1.5 animate-pulse">
                                <AlertOctagon className="w-3.5 h-3.5 flex-shrink-0" />
                                {searchError}
                            </div>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                    <Loader2 className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-3" />
                                    <div className="text-slate-400 text-sm">Загрузка устройств...</div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* ✅ Группы устройств (если есть) */}
                                {filteredGroups.map(group => (
                                    <DeviceTree
                                        key={`group-${group.id}`}
                                        title={`${group.name} (${group.deviceCount})`}
                                        tree={group.tree}
                                        onSelect={handleNodeSelect}
                                        expandedPath={expandedPath}
                                        defaultOpen={true} // ✅ Группы всегда раскрыты по умолчанию
                                        icon={Users} // ✅ Иконка для групп
                                    />
                                ))}

                                {/* ✅ Стандартные деревья (сворачиваются если есть группы) */}
                                <DeviceTree
                                    title={`Обслуживаемые (${devices.counts.onService})`}
                                    tree={filteredDevices.onService}
                                    onSelect={handleNodeSelect}
                                    expandedPath={expandedPath}
                                    defaultOpen={defaultStandardTreesOpen} // ✅ Зависит от наличия групп
                                />
                                <DeviceTree
                                    title={`Все устройства (${devices.counts.all})`}
                                    tree={filteredDevices.all}
                                    onSelect={handleNodeSelect}
                                    expandedPath={expandedPath}
                                    defaultOpen={defaultStandardTreesOpen} // ✅ Зависит от наличия групп
                                />
                            </>
                        )}
                    </div>
                </div>
                <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden flex-1 transition-all duration-300" style={{ minHeight: isDesktop ? 'calc(100vh - 200px)' : 'auto' }}>
                    {renderSelectedNode()}
                </div>
            </div>
        </div>
    );
};

export default DevicesPage;